/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { capitalize } from 'lodash'
import { useDispatch } from 'react-redux'

import {
  EuiCallOut,
  EuiConfirmModal,
  EuiFlexGroup,
  EuiForm,
  EuiFormRow,
  EuiTextArea,
  useGeneratedHtmlId,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { BillingSubscriptionLevel } from '@modules/ui-types'
import useUpdateSubscription from '@modules/billing-lib/mutations/useUpdateSubscription'
import { addToast } from '@modules/cui/Toasts'
import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'

import { browserEnv } from '@/lib/browserEnv'
import { fetchSaasOrganization } from '@/actions/saasOrganizations'

interface Props {
  onClose: () => void
  selectedLevel: BillingSubscriptionLevel
  organizationId: string
}

const ConfirmModal = ({ onClose, selectedLevel, organizationId }: Props) => {
  const modalTitleId = useGeneratedHtmlId()
  const dispatch = useDispatch()
  const billingDetails = useBillingDetails()
  const { formatMessage } = useIntl()
  const [reason, setReason] = useState<string>('')
  const updateSubscriptionMutation = useUpdateSubscription()
  const receivedValidationError = updateSubscriptionMutation.error?.statusCode === 402

  const onConfirm = () => {
    updateSubscriptionMutation.mutate(
      {
        organizationId,
        skipValidations: receivedValidationError,
        body: {
          reason: reason || 'Admin triggers subscription level update',
          level: selectedLevel,
          ...browserEnv(),
        },
      },
      {
        onSuccess: () => {
          addToast({
            color: 'success',
            title: (
              <FormattedMessage
                id='change-subscription.confirm-modal.success'
                defaultMessage='Change requested'
              />
            ),
            text: (
              <FormattedMessage
                id='change-subscription.confirm-modal.success.text'
                defaultMessage="The customer's subscription is being updated. This process may take several minutes."
              />
            ),
          })
          onClose()
          dispatch(fetchSaasOrganization({ organizationId }))
          billingDetails.refetch()
        },
      },
    )
  }

  const renderErrorMessage = () => {
    if (updateSubscriptionMutation.isError) {
      const errors = receivedValidationError
        ? updateSubscriptionMutation.error.detail.messages
        : [
            updateSubscriptionMutation.error.message ||
              (updateSubscriptionMutation.error as unknown as string),
          ]

      return (
        <EuiCallOut
          title={
            <FormattedMessage
              id='change-subscription.confirm-modal.validation-error'
              defaultMessage='Subscription level change could not be requested'
            />
          }
          color='danger'
          iconType='error'
        >
          <ul>
            {errors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </EuiCallOut>
      )
    }

    return null
  }

  const getConfirmButtonText = () => {
    if (receivedValidationError) {
      return formatMessage({
        id: 'change-subscription.confirm-modal.confirm-skip-validation',
        defaultMessage: 'Proceed without validations',
      })
    }

    return formatMessage({
      id: 'change-subscription.confirm-modal.confirm',
      defaultMessage: 'Request change',
    })
  }

  const getContentText = () => {
    if (receivedValidationError) {
      return (
        <FormattedMessage
          id='change-subscription.confirm-modal.content.skip-validation'
          defaultMessage={`Requesting a subscription level change failed for the following reasons. You can still skip validations and continue requesting the customer's subscription level change.`}
        />
      )
    }

    return (
      <FormattedMessage
        id='change-subscription.confirm-modal.content'
        defaultMessage={`The customer's subscription level and benefits will upgrade shortly, while a downgrade may not take place for up to 30 days. Do you want to continue?`}
      />
    )
  }

  const renderReasonInput = () => {
    if (!receivedValidationError) {
      return null
    }

    return (
      <EuiForm fullWidth={true}>
        <EuiFormRow
          label={
            <FormattedMessage
              id='change-subscription.confirm-modal.reason-label'
              defaultMessage='Reason'
            />
          }
          helpText={
            <FormattedMessage
              id='change-subscription.confirm-modal.reason-help-text'
              defaultMessage='Reason for the subscription level change.'
            />
          }
        >
          <EuiTextArea
            data-test-id='change-subscription-reason'
            value={reason}
            rows={2}
            onChange={(e) => setReason(e.target.value)}
            readOnly={updateSubscriptionMutation.isLoading}
          />
        </EuiFormRow>
      </EuiForm>
    )
  }

  return (
    <EuiConfirmModal
      aria-labelledby={modalTitleId}
      title={formatMessage(
        {
          id: 'change-subscription.confirm-modal.title',
          defaultMessage: 'Request change to {level}',
        },
        { level: capitalize(selectedLevel) },
      )}
      titleProps={{ id: modalTitleId }}
      onCancel={onClose}
      onConfirm={onConfirm}
      cancelButtonText={formatMessage({
        id: 'change-subscription.confirm-modal.cancel',
        defaultMessage: 'Cancel',
      })}
      confirmButtonText={getConfirmButtonText()}
      defaultFocusedButton='confirm'
      confirmButtonDisabled={
        updateSubscriptionMutation.isLoading || (!reason && receivedValidationError)
      }
    >
      <EuiFlexGroup direction='column' gutterSize='l'>
        {getContentText()}
        {renderErrorMessage()}
        {renderReasonInput()}
      </EuiFlexGroup>
    </EuiConfirmModal>
  )
}

export default ConfirmModal
